import type { CSSProperties, ReactNode } from 'react'
import { Header } from '@/components/layout/Header'
import {
  MAIN_CONTENT_ID,
  SkipToContent,
} from '@/components/layout/SkipToContent'
import { SidebarInset } from '@/components/ui/sidebar'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'

import { PersonalSidebar } from './PersonalSidebar'

import '../personal.css'

interface PersonalShellProps {
  children: ReactNode
  /** Right-hand pane beside the content (see AppSidebar's `aside`). */
  aside?: ReactNode
}

/**
 * Layout shell for PERSONAL spaces — swapped in for AppSidebar by MainLayout.
 *
 * Mirrors AppSidebar's frame contract (app-shell-frame → Sidebar +
 * SidebarInset → Header → content surface) so every existing page renders
 * unchanged inside it, but owns its own sidebar and the hearth chrome. Work
 * orgs never mount this component.
 */
export function PersonalShell({ children, aside }: PersonalShellProps) {
  const isMobile = useIsMobile()
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const isDesktopTauri = isTauri() && !isMobile

  return (
    <div
      className="app-shell-frame ph-shell flex h-full min-w-0 flex-1"
      style={{ '--sidebar-width': '15rem' } as CSSProperties}
    >
      <SkipToContent />
      <PersonalSidebar />
      <SidebarInset
        className={cn(
          'app-shell-inset ph-inset flex h-full min-h-0 w-full min-w-0 flex-col',
          isDesktopTauri
            ? tauriGlassEnabled
              ? 'bg-transparent'
              : 'bg-background'
            : 'bg-background',
          !isMobile && 'mb-4 ml-0',
          !isMobile && !isDesktopTauri && 'shadow-sm',
        )}
        tauriOffset={isTauri() && !isMobile}
      >
        <Header />
        <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
          {isDesktopTauri && (
            <div
              aria-hidden
              className={cn(
                'app-shell-corner-notch pointer-events-none absolute left-0 top-0 z-0 h-4 w-4',
                tauriGlassEnabled ? 'tauri-glass-corner-fill' : 'bg-sidebar',
              )}
            />
          )}
          <div
            id={MAIN_CONTENT_ID}
            tabIndex={-1}
            className="app-shell-content-surface relative z-10 h-full min-h-0 min-w-0 overflow-hidden rounded-tl-2xl"
          >
            {children}
          </div>
        </div>
      </SidebarInset>
      {aside}
    </div>
  )
}
